/**
 * Age Badge Component
 * ===================
 * Displays verified age badge on user profiles.
 * Special indicator for 21+ members (legal drinking age).
 * 
 * Features:
 * - Age verification status badge
 * - 21+ indicator with special styling
 * - Under 21 indicator (age must be visible for these users)
 */

import React from 'react';
import { Check, Wine, ShieldCheck, Calendar } from 'lucide-react';

const AgeBadge = ({ 
  age, 
  isVerified = false,
  isUnder21 = null,
  size = 'medium',
  showAge = true,
  style = {}
}) => {
  // Don't render if no age data
  if (!age && !isVerified) {
    return null;
  }

  // Determine if 21+
  const is21Plus = age && age >= 21;
  const isUnder21Display = isUnder21 !== null ? isUnder21 : (age ? age < 21 : null);

  const sizes = {
    small: { padding: '3px 8px', fontSize: '9px', iconSize: 10, gap: '4px' },
    medium: { padding: '5px 12px', fontSize: '11px', iconSize: 12, gap: '5px' },
    large: { padding: '6px 16px', fontSize: '13px', iconSize: 14, gap: '6px' }
  };

  const sizeConfig = sizes[size] || sizes.medium;

  // 21+ verified badge styling
  if (is21Plus && isVerified) {
    return (
      <div 
        data-testid="age-badge-21plus"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: sizeConfig.gap,
          padding: sizeConfig.padding,
          background: 'linear-gradient(135deg, #6B46C1 0%, #553C9A 50%, #44337A 100%)',
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(107, 70, 193, 0.4)',
          border: '1px solid rgba(159, 122, 234, 0.4)',
          ...style
        }}
      >
        <Wine 
          size={sizeConfig.iconSize} 
          color="#E9D8FD"
          style={{ filter: 'drop-shadow(0 0 2px rgba(159, 122, 234, 0.5))' }}
        />
        <span style={{
          fontSize: sizeConfig.fontSize,
          fontWeight: '700',
          color: '#E9D8FD',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          21+
        </span>
        <ShieldCheck 
          size={sizeConfig.iconSize - 2} 
          color="#68D391"
          style={{ marginLeft: '-2px' }}
        />
      </div>
    );
  }

  // Under 21 verified badge (age must be displayed)
  if (isUnder21Display && isVerified) {
    return (
      <div 
        data-testid="age-badge-under21"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: sizeConfig.gap,
          padding: sizeConfig.padding,
          background: 'linear-gradient(135deg, #2D3748 0%, #1A202C 100%)',
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(165, 124, 51, 0.3)',
          ...style
        }}
      >
        <Calendar 
          size={sizeConfig.iconSize} 
          color="#F6AD55"
        />
        {showAge && age && (
          <span style={{
            fontSize: sizeConfig.fontSize,
            fontWeight: '700',
            color: '#F6AD55',
            letterSpacing: '0.5px'
          }}>
            {age}
          </span>
        )}
        <Check 
          size={sizeConfig.iconSize - 2} 
          color="#68D391"
          style={{ marginLeft: '-2px' }}
        />
      </div>
    );
  }

  // Generic verified age badge
  if (isVerified) {
    return (
      <div 
        data-testid="age-badge-verified"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: sizeConfig.gap,
          padding: sizeConfig.padding,
          background: 'linear-gradient(135deg, #2D3748 0%, #1A202C 100%)',
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(72, 187, 120, 0.3)',
          ...style
        }}
      >
        <ShieldCheck 
          size={sizeConfig.iconSize} 
          color="#68D391"
        />
        {showAge && age && (
          <span style={{
            fontSize: sizeConfig.fontSize,
            fontWeight: '600',
            color: '#E6D7C3',
            letterSpacing: '0.5px'
          }}>
            Age Verified
          </span>
        )}
      </div>
    );
  }

  // Unverified - show age only (for display purposes)
  if (showAge && age) {
    return (
      <div 
        data-testid="age-badge-unverified"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: sizeConfig.gap,
          padding: sizeConfig.padding,
          background: 'rgba(165, 124, 51, 0.1)',
          borderRadius: '20px',
          border: '1px solid rgba(165, 124, 51, 0.2)',
          ...style
        }}
      >
        <Calendar 
          size={sizeConfig.iconSize} 
          color="#A57C33"
        />
        <span style={{
          fontSize: sizeConfig.fontSize,
          fontWeight: '500',
          color: '#A57C33',
          letterSpacing: '0.5px'
        }}>
          {age}
        </span>
      </div>
    );
  }

  return null;
};

export default AgeBadge;
